import type { EquipmentListItem, EquipmentSort, TicketListItem } from '@erp/shared';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { formatDay, formatMoment, todayIso } from '@/lib/format';
import { StageBadge } from './crm-bits';
import { LifecycleBadge, Person, PriorityBadge, SlaMark, TicketStatusBadge } from './equipment-bits';
import { type ColumnDef, type ColumnsState, SortHeader, type SortState } from './list-tools';

/** Таблицы оборудования и заявок — в разделах и в карточках CRM. */

export function Warranty({ until }: { until: string | null }) {
  if (!until) return <span className="text-steel">—</span>;
  const expired = until < todayIso();
  return (
    <span className={clsx(expired && 'text-danger')}>
      {formatDay(until)}
      {expired && <span className="block text-[12px]">истекла</span>}
    </span>
  );
}

export type EquipmentColumn = 'client' | 'stage' | 'assembled' | 'shipped' | 'warranty' | 'tickets' | 'deal';
export const EQUIPMENT_COLUMNS: ColumnDef<EquipmentColumn>[] = [
  { key: 'client', label: 'Клиент' },
  { key: 'stage', label: 'Состояние' },
  { key: 'assembled', label: 'Собран' },
  { key: 'shipped', label: 'Отгружен' },
  { key: 'warranty', label: 'Гарантия' },
  { key: 'deal', label: 'Сделка', defaultHidden: true },
  { key: 'tickets', label: 'Заявки' },
];

export function EquipmentTable({
  items,
  compact,
  sort,
  onSort,
  cols,
}: {
  items: EquipmentListItem[];
  compact?: boolean;
  sort?: SortState<EquipmentSort>;
  onSort?: (s: SortState<EquipmentSort>) => void;
  cols?: ColumnsState<EquipmentColumn>;
}) {
  const head = (label: string, key: EquipmentSort, align?: 'right') =>
    sort && onSort ? (
      <SortHeader label={label} sortKey={key} state={sort} onSort={onSort} align={align} />
    ) : (
      <th scope="col" className={clsx('px-4 py-2.5 font-medium', align === 'right' && 'text-right')}>
        {label}
      </th>
    );
  const wide = !compact;
  const show = (k: EquipmentColumn) =>
    cols
      ? cols.show(k)
      : compact
        ? ['stage', 'warranty', 'tickets'].includes(k)
        : !EQUIPMENT_COLUMNS.find((c) => c.key === k)?.defaultHidden;
  return (
    <div className="panel relative overflow-x-auto">
      <table className={clsx('w-full text-left', wide && 'min-w-[980px]')}>
        <thead>
          <tr className="border-b border-line text-[13px] text-steel">
            {head('Оборудование', 'serial')}
            {show('client') && head('Клиент', 'client')}
            {show('stage') && head('Состояние', 'stage')}
            {show('assembled') && head('Собран', 'assembled')}
            {show('shipped') && head('Отгружен', 'shipped')}
            {show('warranty') && head('Гарантия', 'warranty')}
            {show('deal') && (
              <th scope="col" className="px-4 py-2.5 font-medium">
                Сделка
              </th>
            )}
            {show('tickets') && head('Заявки', 'tickets', 'right')}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {items.map((e) => {
            const waitingCase = e.caseExpectedAt && !e.assembledAt;
            const waitingBoard = e.boardExpectedAt && !e.assembledAt;
            return (
              <tr key={e.id} className="hover:bg-paper/70">
                <td className="px-4 py-2.5">
                  <Link to="/equipment/$id" params={{ id: e.id }} className="font-medium hover:text-link">
                    {e.title}
                  </Link>
                  <span className="block text-[12.5px] text-steel">
                    {[e.boardId && `плата ${e.boardId}`, e.location].filter(Boolean).join(' · ') || ' '}
                  </span>
                </td>
                {show('client') && (
                  <td className="px-4 py-2.5">
                    {e.contact?.fullName ?? e.company?.name ?? <span className="text-steel">—</span>}
                    {e.contact && e.company && (
                      <span className="block text-[12.5px] text-steel">{e.company.name}</span>
                    )}
                  </td>
                )}
                {show('stage') && (
                  <td className="px-4 py-2.5">
                    {e.lifecycle === 'production' && e.stage ? (
                      <StageBadge stage={e.stage} />
                    ) : (
                      <LifecycleBadge lifecycle={e.lifecycle} />
                    )}
                    {(waitingCase || waitingBoard) && (
                      <span className="block text-[12.5px] text-[#7a5b00]">
                        {[
                          waitingCase && `корпус ${formatDay(e.caseExpectedAt)}`,
                          waitingBoard && `плата ${formatDay(e.boardExpectedAt)}`,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      </span>
                    )}
                    {compact && e.shippedAt && e.lifecycle !== 'production' && (
                      <span className="block text-[12.5px] text-steel">
                        отгружен {formatDay(e.shippedAt)}
                      </span>
                    )}
                  </td>
                )}
                {show('assembled') && (
                  <td className="px-4 py-2.5 whitespace-nowrap text-ink-soft">
                    {e.assembledAt ? formatDay(e.assembledAt) : <span className="text-steel">—</span>}
                  </td>
                )}
                {show('shipped') && (
                  <td className="px-4 py-2.5 whitespace-nowrap text-ink-soft">
                    {e.shippedAt ? formatDay(e.shippedAt) : <span className="text-steel">—</span>}
                  </td>
                )}
                {show('warranty') && (
                  <td className="px-4 py-2.5">
                    <Warranty until={e.warrantyUntil} />
                  </td>
                )}
                {show('deal') && (
                  <td className="px-4 py-2.5">
                    {e.deal ? (
                      <Link to="/crm/deals/$id" params={{ id: e.deal.id }} className="link">
                        #{e.deal.number} {e.deal.title}
                      </Link>
                    ) : (
                      <span className="text-steel">—</span>
                    )}
                  </td>
                )}
                {show('tickets') && (
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {e.openTickets > 0 ? (
                      <span className="font-medium text-danger">{e.openTickets}</span>
                    ) : (
                      <span className="text-steel">—</span>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function TicketTable({ items, compact }: { items: TicketListItem[]; compact?: boolean }) {
  return (
    <div className="panel overflow-x-auto">
      <table className={clsx('w-full text-left', !compact && 'min-w-[900px]')}>
        <thead>
          <tr className="border-b border-line text-[13px] text-steel">
            <th scope="col" className="w-16 px-4 py-2.5 font-medium">
              №
            </th>
            <th scope="col" className="px-4 py-2.5 font-medium">
              Заявка
            </th>
            <th scope="col" className="px-4 py-2.5 font-medium">
              Статус
            </th>
            {!compact && (
              <th scope="col" className="px-4 py-2.5 font-medium">
                Исполнитель
              </th>
            )}
            <th scope="col" className="px-4 py-2.5 font-medium">
              Срок
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {items.map((t) => (
            <tr
              key={t.id}
              className={clsx('hover:bg-paper/70', !t.assignee && t.status === 'new' && 'bg-signal-soft/40')}
            >
              <td className="px-4 py-2.5 tabular-nums text-steel">{t.number}</td>
              <td className="px-4 py-2.5">
                <Link to="/helpdesk/$id" params={{ id: t.id }} className="font-medium hover:text-link">
                  {t.subject}
                </Link>
                <span className="block text-[12.5px] text-steel">
                  {[t.contact?.fullName ?? t.company?.name, !compact && t.equipment?.title, t.category?.name]
                    .filter(Boolean)
                    .join(' · ') || formatMoment(t.createdAt)}
                </span>
              </td>
              <td className="px-4 py-2.5">
                <span className="flex flex-wrap items-center gap-1.5">
                  <TicketStatusBadge status={t.status} />
                  {(t.priority === 'high' || t.priority === 'critical') && (
                    <PriorityBadge priority={t.priority} />
                  )}
                </span>
              </td>
              {!compact && (
                <td className="px-4 py-2.5">
                  <Person user={t.assignee} empty="никто" />
                </td>
              )}
              <td className="px-4 py-2.5">
                <SlaMark ticket={t} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
